import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { equipmentApi, type EquipmentRate, type EquipmentProjectOption } from '@/lib/apiClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { RatePicker } from './RatePicker';

type ProjectRef = EquipmentProjectOption;

// Compact read-only label/value row used to surface project-derived info
// (field leader, job address, site contact) that isn't an editable field.
// Kept local so the form is self-contained.
function ReadOnlyRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 12, lineHeight: 1.4 }}>
      <span style={{ color: 'var(--ew-text-subtle, #6B7280)', flexShrink: 0, minWidth: 86 }}>{label}</span>
      <span style={{ fontWeight: 500, color: 'var(--ew-text, #1F2937)' }}>{value}</span>
    </div>
  );
}

export interface EquipmentRequestFormProps {
  /** Full project-options list used to auto-match a typed job number. */
  projects: ProjectRef[];
  onClose: () => void;
  onSaved: () => void;
  /**
   * When set (project-workspace use), the request is pre-scoped to this job:
   * the Job Number / Job Name inputs are replaced by a read-only chip and the
   * payload submits `projectId = lockedProject.id` directly (no job-# match).
   * The global Equipment page leaves this undefined.
   */
  lockedProject?: { id: number; jobNumber: string; title: string };
}

/**
 * Reusable "New Equipment Request" drawer. Extracted from EquipmentPage so the
 * project workspace can open it pre-scoped to a job (via `lockedProject`) while
 * the global Equipment page keeps the free-entry job-number flow.
 */
export default function EquipmentRequestForm({ projects, onClose, onSaved, lockedProject }: EquipmentRequestFormProps) {
  const qc = useQueryClient();
  const drawerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(drawerRef, { onEscape: onClose });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [jobNumber, setJobNumber]         = useState('');
  const [jobName, setJobName]             = useState('');
  const [equipmentType, setEquipmentType] = useState('');
  const [pickedRate, setPickedRate]       = useState<EquipmentRate | null>(null);
  const [attachments, setAttachments]     = useState('');
  // Free-text spec for the piece being asked for — lift size, style, capacity,
  // reach. Requested by production 2026-07-28: Equipment Type alone ("Boom
  // lift") doesn't tell the yard which one to send. Writes to the SAME notes
  // field the edit drawer shows, and is already carried on the new-request
  // webhook, so ops get it on the Teams card without any backend change.
  const [notes, setNotes]                 = useState('');
  const [deliveryDate, setDeliveryDate]   = useState('');
  // When the crew actually needs it, if that's later than the drop-off —
  // "drop equipment Friday, start Monday" (#306). Optional: for most requests
  // delivery and start are the same day and one date is enough.
  const [startDate, setStartDate]         = useState('');
  const [endDate, setEndDate]             = useState('');
  // How many units. Rate is PER UNIT, so this multiplies the cost.
  const [quantity, setQuantity]           = useState('1');
  const [costCode, setCostCode]           = useState('');

  // Locked → resolve the richer project option (field leader / address / site
  // contact) by id so the info panel still shows. Otherwise auto-match the
  // typed job number, the same way the global page does.
  const matchedProject = lockedProject
    ? projects.find(p => p.id === lockedProject.id)
    : projects.find(p => p.jobNumber.trim() === jobNumber.trim());

  const handleJobNumberChange = (val: string) => {
    setJobNumber(val);
    const match = projects.find(p => p.jobNumber.trim() === val.trim());
    if (match) setJobName(match.title);
  };

  const handleSave = async () => {
    if (!equipmentType.trim()) { setSaveError('Equipment Type is required.'); return; }
    if (!deliveryDate)          { setSaveError('Delivery Date is required.'); return; }
    if (!endDate)               { setSaveError('End Date is required.'); return; }
    setSaving(true); setSaveError(null);
    try {
      await equipmentApi.create({
        title:                equipmentType.trim(),
        // Locked: submit this job's id directly. Free entry: use the matched
        // project's id, falling back to a raw job name when unmatched.
        projectId:            lockedProject?.id ?? matchedProject?.id ?? null,
        jobNameRaw:           lockedProject ? null : (matchedProject ? null : (jobName.trim() || jobNumber.trim() || null)),
        equipmentType:        equipmentType.trim(),
        attachmentDescription: attachments.trim() || null,
        deliveryDate:         deliveryDate || null,
        startDate:            startDate || null,
        endDate:              endDate || null,
        quantity:             Math.max(1, Number(quantity) || 1),
        costCode:             costCode.trim() || null,
        notes:                notes.trim() || null,
        statusId:             1,
      });
      qc.invalidateQueries({ queryKey: ['equipment'] });
      onSaved();
    } catch (err: any) {
      setSaveError(err.message);
    }
    setSaving(false);
  };

  const inp: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, width: '100%', fontFamily: 'inherit', background: '#fff', color: 'var(--text)' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '.5px', color: 'var(--gray)', marginBottom: 3, display: 'block' };
  const fw:  React.CSSProperties = { display: 'flex', flexDirection: 'column' as const };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 200 }} />
      <div ref={drawerRef} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 480, background: '#fff', zIndex: 201, overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>New Equipment Request</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ fontSize: 12, color: 'var(--gray)', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '5px 10px', background: '#fff', cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ fontSize: 12, background: 'var(--ew)', color: '#fff', border: 'none', borderRadius: 'var(--ew-r-md)', padding: '6px 14px', cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Submitting…' : 'Submit Request'}
            </button>
          </div>
        </div>

        {saveError && <div style={{ margin: '8px 18px 0', padding: '8px 12px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{saveError}</div>}

        <div style={{ padding: '20px 18px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {lockedProject ? (
            // Project-workspace use: pre-scoped to this job, not changeable.
            <div style={fw}>
              <label style={lbl}>Job</label>
              <div style={{ ...inp, background: 'var(--surface)', display: 'flex', alignItems: 'center' }}>
                #{lockedProject.jobNumber} — {lockedProject.title}
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0 14px' }}>
              <div style={fw}>
                <label style={lbl}>Job Number <span style={{ color: '#b91c1c' }}>*</span></label>
                <input style={inp} value={jobNumber} onChange={e => handleJobNumberChange(e.target.value)} placeholder="e.g. 10900" />
              </div>
              <div style={fw}>
                <label style={lbl}>Job Name <span style={{ color: '#b91c1c' }}>*</span></label>
                <input style={inp} value={jobName} onChange={e => setJobName(e.target.value)} placeholder="Auto-filled from job number" />
                {matchedProject && <span style={{ fontSize: 10, color: 'var(--ew)', marginTop: 2 }}>✓ Linked to project #{matchedProject.jobNumber}</span>}
              </div>
            </div>
          )}

          {matchedProject && (matchedProject.fieldLeader || matchedProject.jobAddress || matchedProject.siteContactName) && (
            <div style={{ background: 'var(--ew-bg-subtle, #F6F7F9)', border: '1px solid var(--ew-border, #E2E5EA)', borderRadius: 'var(--ew-r-md)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ew-text-subtle, #6B7280)' }}>From this project</div>
              {matchedProject.fieldLeader && <ReadOnlyRow label="Field Leader" value={matchedProject.fieldLeader} />}
              {matchedProject.jobAddress && <ReadOnlyRow label="Job Address" value={matchedProject.jobAddress} />}
              {matchedProject.siteContactName && (
                <ReadOnlyRow label="Site Contact" value={[matchedProject.siteContactName, matchedProject.siteContactPhone].filter(Boolean).join(' · ')} />
              )}
            </div>
          )}

          <div style={fw}>
            <label style={lbl}>Equipment Type <span style={{ color: '#b91c1c' }}>*</span></label>
            <RatePicker
              value={equipmentType}
              selectedRate={pickedRate}
              onChange={({ equipmentType: et, rate }) => {
                setEquipmentType(et);
                setPickedRate(rate);
              }}
            />
          </div>

          <div style={fw}>
            <label style={lbl}>Equipment Attachments Needed</label>
            <input style={inp} value={attachments} onChange={e => setAttachments(e.target.value)} placeholder="e.g. Auger, Bucket, Forks (optional)" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <div style={fw}>
              <label style={lbl}>Delivery Date <span style={{ color: '#b91c1c' }}>*</span></label>
              <input type="date" style={inp} value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} />
            </div>
            <div style={fw}>
              {/* Optional, and not required alongside the two mandatory dates:
                  most requests are delivered and started the same day, so
                  forcing a third date would be noise on every one of them. */}
              <label style={lbl}>Start Date</label>
              <input type="date" style={inp} value={startDate} min={deliveryDate || undefined} max={endDate || undefined}
                onChange={e => setStartDate(e.target.value)}
                title="When the crew actually needs it, if later than delivery — e.g. dropped Friday, started Monday" />
            </div>
            <div style={fw}>
              <label style={lbl}>End Date <span style={{ color: '#b91c1c' }}>*</span></label>
              <input type="date" style={inp} value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>
            <div style={fw}>
              <label style={lbl}>Quantity</label>
              <input type="number" min={1} step={1} style={inp} value={quantity}
                onChange={e => setQuantity(e.target.value)}
                title="How many units — e.g. 3 light towers. The rate is per unit." />
            </div>
          </div>

          <div style={fw}>
            <label style={lbl}>Cost Code <span style={{ color: '#b91c1c' }}>*</span></label>
            <input style={inp} value={costCode} onChange={e => setCostCode(e.target.value)} placeholder="e.g. 01-500" />
          </div>

          {/* Equipment specifics. Deliberately last and roomy: it is the field
              most likely to be written in sentences rather than a value. */}
          <div style={fw}>
            <label style={lbl}>Notes / Equipment Details</label>
            <textarea
              style={{ ...inp, minHeight: 84, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.45 }}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={'Lift size and style, reach or working height, capacity, tires vs tracks, access restrictions — anything the yard needs to pick the right machine.\n\ne.g. 60ft articulating boom lift, rough terrain, must fit through a 10ft gate'}
            />
            <span style={{ fontSize: 11, color: 'var(--gray)', marginTop: 4 }}>
              Goes straight to whoever fills the request — the more specific, the fewer phone calls.
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
